import {
  GridColDef,
  GridInitialState,
  GridRenderCellParams,
} from "@mui/x-data-grid-premium";
import { Box } from "@mui/material";
import React, { useEffect, useState } from "react";
import useApi from "../../hooks/useGaneshApi";
import { renderNumberCell } from "utils/dataGrid";
import GaneshDataGrid from "../DataGrid/GaneshDataGrid";
import AnalysisJobDetailsModal from "./AnalysisJobDetailsModal";
import { placeSetTypeToDatasetType } from "../../types/Compliance";

import { useError } from "../../contexts/ErrorContext";

type AnalysisJobsSummary_T = {
  admin_id: number;
  admin_name: string;
  place_set_type: string;
  export_type: string;
  queued: number;
  in_progress: number;
  jobs: number;
};

const defaultGridState: GridInitialState = {
  filter: {
    filterModel: { items: [] },
  },
  sorting: { sortModel: [{ field: "admin_id", sort: "asc" }] },
  pagination: { paginationModel: { pageSize: 25, page: 0 } },
  pinnedColumns: { left: [] },
  columns: { columnVisibilityModel: {} },
  density: "compact",
};

const ActiveExportJobsSummary: React.FC<any> = () => {
  const [analysisJobsSummaryData, setAnalysisJobsSummaryData] = useState<
    AnalysisJobsSummary_T[]
  >([]);
  const [loading, setLoading] = useState(true);

  const { showError } = useError();
  const { apiRequest } = useApi();
  const [openModalKey, setOpenModalKey] = useState<string | null>(null);

  useEffect(() => {
    const fetchAnalysisJobsSummary = async () => {
      try {
        const response = await apiRequest<{ data: AnalysisJobsSummary_T[] }>(
          `/v1/export-jobs/summary`,
          { method: "GET" }
        );
        if (response.data) {
          // Handle both old and new response formats
          const data = response.data.data || response.data;
          setAnalysisJobsSummaryData(data);
        }
      } catch (error) {
        console.error(error);
        showError(`Failed to fetch all deliveries.`);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalysisJobsSummary();
  }, []);

  const columns_def: GridColDef[] = [
    {
      field: "admin_id",
      headerName: "Admin ID",
      width: 100,
      type: "number",
    },
    {
      field: "admin_name",
      headerName: "Admin Name",
      width: 200,
      type: "string",
    },
    {
      field: "place_set_type",
      headerName: "Place Set Type",
      width: 150,
    },
    {
      field: "export_type",
      headerName: "Export Type",
      width: 150,
    },
    {
      field: "queued",
      headerName: "Queued Jobs",
      type: "number",
      width: 150,
      renderCell: renderNumberCell,
    },
    {
      field: "in_progress",
      headerName: "In Progress Jobs",
      type: "number",
      width: 150,
      renderCell: renderNumberCell,
    },
    {
      field: "jobs",
      headerName: "Jobs",
      width: 130,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => {
        const datasetType = placeSetTypeToDatasetType[row.place_set_type];

        if (!datasetType) return null;

        return (
          <Box display="flex" alignItems="center" height="100%">
            <AnalysisJobDetailsModal
              adminId={row.admin_id}
              datasetType={datasetType}
              label="Active Jobs"
              isOpen={
                openModalKey ===
                `${row.admin_id}-${row.place_set_type}-${row.export_type}`
              }
              onOpen={() =>
                setOpenModalKey(
                  `${row.admin_id}-${row.place_set_type}-${row.export_type}`
                )
              }
              onClose={() => setOpenModalKey(null)}
              endpoint="active"
              exportType={row.export_type}
            />
          </Box>
        );
      },
    },
  ];

  return (
    <GaneshDataGrid
      loading={loading}
      data={analysisJobsSummaryData}
      columns={columns_def}
      gridId="analysisJobsSummaryGrid"
      defaultGridState={defaultGridState}
    />
  );
};
export default ActiveExportJobsSummary;
